import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

const B = {
  gold: "#D4A843", green: "#43B581", red: "#E74C3C", orange: "#FF6B35",
  blue: "#5DADE2", bg: "#0D0F13", card: "#13161C", border: "#1E222A",
  white: "#F0F0F0", whiteGray: "#A0A8B8", gray: "#6B7280",
};

function authHeaders() {
  const token = localStorage.getItem("flexx_jwt");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function StatTile({ label, value, sub, color = B.white, testid }) {
  return (
    <div data-testid={testid} style={{
      background: B.card, border: `1px solid ${B.border}`, borderRadius: 12,
      padding: "16px 20px", flex: "1 1 200px", minWidth: 200,
    }}>
      <div style={{ color: B.gray, fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 }}>{label}</div>
      <div style={{ color, fontSize: 22, fontWeight: 800, lineHeight: 1.1, fontFamily: "monospace", wordBreak: "break-word" }}>{value}</div>
      {sub && <div style={{ color: B.whiteGray, fontSize: 11, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

export default function AxelSystemHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [diff, setDiff] = useState(null); // { user, system_prompt, ... }
  const [diffLoading, setDiffLoading] = useState(false);

  const fetchDiag = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/axel/diagnostics`, {
        ...ax, headers: authHeaders(),
      });
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
  }, []);

  const openDiff = useCallback(async (userId) => {
    setDiffLoading(true);
    setDiff({ loading: true });
    try {
      const res = await axios.get(`${API}/admin/axel/last-prompt`, {
        ...ax, headers: authHeaders(),
        params: { user_id: userId },
      });
      setDiff(res.data);
    } catch (e) {
      setDiff({ error: e?.response?.data?.detail || e.message || "Failed to load prompt" });
    } finally {
      setDiffLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDiag();
    const id = setInterval(fetchDiag, 30 * 1000); // refresh every 30s
    return () => clearInterval(id);
  }, [fetchDiag]);

  if (loading) {
    return <div data-testid="axel-system-health-loading" style={{ padding: 24, color: B.whiteGray }}>Loading AXEL system health…</div>;
  }
  if (err) {
    return <div data-testid="axel-system-health-error" style={{ padding: 24, color: B.red }}>{err}</div>;
  }
  if (!data) return null;

  const lastCallSec = data.last_successful_call_at
    ? Math.round((Date.now() - new Date(data.last_successful_call_at).getTime()) / 1000)
    : Infinity;
  const liveColor = lastCallSec < 60 * 5 ? B.green : lastCallSec < 60 * 60 ? B.orange : B.red;

  return (
    <div data-testid="axel-system-health-panel" style={{ padding: "16px 24px 40px" }}>
      {/* Banner */}
      <div style={{
        background: `${liveColor}10`,
        border: `1px solid ${liveColor}40`,
        borderLeft: `4px solid ${liveColor}`,
        borderRadius: 10, padding: "14px 18px", marginBottom: 18,
      }}>
        <div style={{ color: liveColor, fontSize: 12, fontWeight: 800, letterSpacing: 1.2, marginBottom: 4 }}>
          ● AXEL SYSTEM HEALTH
        </div>
        <div style={{ color: B.whiteGray, fontSize: 13 }}>
          Last successful API call: <strong style={{ color: liveColor }} data-testid="axel-last-call-relative">
            {relativeTime(data.last_successful_call_at)}
          </strong>
          {data.last_successful_call_at && (
            <span style={{ color: B.gray, fontSize: 11, marginLeft: 8 }}>
              ({new Date(data.last_successful_call_at).toLocaleString()})
            </span>
          )}
        </div>
      </div>

      {/* Stat tiles */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <StatTile
          testid="health-stat-model"
          label="Active Model"
          value={data.axel_model}
          sub={data.axel_model_last_used && data.axel_model_last_used !== data.axel_model
            ? `last used: ${data.axel_model_last_used}` : "Anthropic via emergentintegrations"}
          color={B.gold}
        />
        <StatTile
          testid="health-stat-prompt-chars"
          label="System Prompt Size"
          value={data.system_prompt_chars.toLocaleString()}
          sub={`${data.system_prompt_tokens_estimate.toLocaleString()} tokens (est.)`}
          color={B.blue}
        />
        <StatTile
          testid="health-stat-cached-chats"
          label="Warm Chat Cache"
          value={data.cached_chats_count}
          sub={`max 500 · evicts oldest 100 when full`}
          color={B.white}
        />
        <StatTile
          testid="health-stat-daily-limit"
          label="Daily Limit / Student"
          value={data.daily_limit_per_student}
          sub="messages per UTC day"
          color={B.white}
        />
      </div>

      {/* Today totals */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <StatTile
          testid="health-totals-messages"
          label="Messages Today"
          value={data.totals_today.messages.toLocaleString()}
          color={B.green}
        />
        <StatTile
          testid="health-totals-users"
          label="Active Users Today"
          value={data.totals_today.active_users}
          color={B.green}
        />
        <StatTile
          testid="health-totals-tokens"
          label="Tokens Today"
          value={data.totals_today.total_tokens.toLocaleString()}
          color={B.green}
        />
        <StatTile
          testid="health-totals-cost"
          label="Spend Today"
          value={`$${data.totals_today.cost_usd.toFixed(4)}`}
          color={B.green}
        />
      </div>

      {/* Per-user table */}
      <div style={{
        background: B.card, border: `1px solid ${B.border}`, borderRadius: 12,
        padding: "16px 20px",
      }}>
        <div style={{ color: B.gold, fontSize: 13, fontWeight: 800, letterSpacing: 1.2, marginBottom: 12, textTransform: "uppercase" }}>
          Per-User Usage Today (top {data.per_user_today.length}) <span style={{ color: B.gray, fontSize: 10, fontWeight: 500, textTransform: "none", letterSpacing: 0, marginLeft: 8 }}>· click any row to see their last system prompt</span>
        </div>
        {data.per_user_today.length === 0 ? (
          <div data-testid="health-per-user-empty" style={{ color: B.gray, fontSize: 13, padding: "12px 0" }}>
            No AXEL traffic yet today.
          </div>
        ) : (
          <table data-testid="health-per-user-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ color: B.gray, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>
                <th style={{ textAlign: "left", padding: "8px 6px", borderBottom: `1px solid ${B.border}` }}>Student</th>
                <th style={{ textAlign: "right", padding: "8px 6px", borderBottom: `1px solid ${B.border}` }}>Msgs</th>
                <th style={{ textAlign: "right", padding: "8px 6px", borderBottom: `1px solid ${B.border}` }}>Tokens</th>
                <th style={{ textAlign: "right", padding: "8px 6px", borderBottom: `1px solid ${B.border}` }}>Cost</th>
                <th style={{ textAlign: "right", padding: "8px 6px", borderBottom: `1px solid ${B.border}` }}>Last</th>
              </tr>
            </thead>
            <tbody>
              {data.per_user_today.map((row) => {
                const overLimit = row.messages >= data.daily_limit_per_student;
                return (
                  <tr key={row.user_id} data-testid={`health-row-${row.user_id}`} onClick={() => openDiff(row.user_id)}
                    style={{ color: B.white, cursor: "pointer", transition: "background 0.15s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = `${B.gold}08`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                    <td style={{ padding: "10px 6px", borderBottom: `1px solid ${B.border}` }}>
                      <div style={{ color: B.white, fontWeight: 600 }}>{row.name || "—"}</div>
                      <div style={{ color: B.gray, fontSize: 11, fontFamily: "monospace" }}>{row.email}</div>
                    </td>
                    <td style={{ padding: "10px 6px", borderBottom: `1px solid ${B.border}`, textAlign: "right", color: overLimit ? B.red : B.white, fontFamily: "monospace", fontWeight: 700 }}>
                      {row.messages}{overLimit ? " · capped" : ""}
                    </td>
                    <td style={{ padding: "10px 6px", borderBottom: `1px solid ${B.border}`, textAlign: "right", color: B.whiteGray, fontFamily: "monospace" }}>
                      {row.total_tokens.toLocaleString()}
                    </td>
                    <td style={{ padding: "10px 6px", borderBottom: `1px solid ${B.border}`, textAlign: "right", color: B.whiteGray, fontFamily: "monospace" }}>
                      ${row.cost_usd.toFixed(4)}
                    </td>
                    <td style={{ padding: "10px 6px", borderBottom: `1px solid ${B.border}`, textAlign: "right", color: B.gray, fontFamily: "monospace", fontSize: 11 }}>
                      {relativeTime(row.last_ts)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ color: B.gray, fontSize: 11, marginTop: 14, fontFamily: "monospace" }}>
        Auto-refresh every 30s · UTC day "{data.today}" · server time {new Date(data.now).toLocaleTimeString()}
      </div>

      {/* Prompt-diff viewer modal */}
      {diff && (
        <div data-testid="prompt-diff-modal" onClick={() => setDiff(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: B.bg, border: `1px solid ${B.gold}40`, borderRadius: 12,
            maxWidth: 1100, width: "100%", maxHeight: "92vh", overflow: "hidden",
            display: "flex", flexDirection: "column",
          }}>
            <div style={{ padding: "16px 22px", borderBottom: `1px solid ${B.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
              <div>
                <div style={{ color: B.gold, fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase" }}>System Prompt Snapshot</div>
                {diff.user && (
                  <div style={{ color: B.white, fontSize: 14, fontWeight: 700, marginTop: 4 }}>
                    {diff.user.name || diff.user.email} <span style={{ color: B.gray, fontSize: 12, fontFamily: "monospace", fontWeight: 400, marginLeft: 8 }}>{diff.user.email}</span>
                  </div>
                )}
                {diff.last_call && (
                  <div style={{ color: B.whiteGray, fontSize: 11, marginTop: 4 }}>
                    Last call: <span style={{ color: B.green, fontFamily: "monospace" }}>{relativeTime(diff.last_call.ts)}</span>
                    <span style={{ color: B.gray, marginLeft: 10 }}>· model {diff.last_call.model}</span>
                    <span style={{ color: B.gray, marginLeft: 10 }}>· {diff.last_call.total_tokens?.toLocaleString()} tokens</span>
                    <span style={{ color: B.gray, marginLeft: 10 }}>· ${(diff.last_call.cost_usd || 0).toFixed(4)}</span>
                  </div>
                )}
              </div>
              <button data-testid="prompt-diff-close" onClick={() => setDiff(null)}
                style={{ padding: "8px 14px", borderRadius: 6, border: `1px solid ${B.border}`, background: "transparent", color: B.whiteGray, fontSize: 12, cursor: "pointer" }}>
                Close
              </button>
            </div>
            <div style={{ padding: "10px 22px", display: "flex", gap: 16, fontSize: 11, color: B.gray, borderBottom: `1px solid ${B.border}`, fontFamily: "monospace" }}>
              {!diffLoading && diff.system_prompt && (
                <>
                  <span data-testid="prompt-diff-chars">{diff.system_prompt_chars?.toLocaleString()} chars</span>
                  <span data-testid="prompt-diff-tokens">{diff.system_prompt_tokens_estimate?.toLocaleString()} tokens (est.)</span>
                  <span>active model: <span style={{ color: B.gold }}>{diff.axel_model}</span></span>
                </>
              )}
            </div>
            <div style={{ overflow: "auto", flex: 1, padding: "0 22px 22px" }}>
              {diffLoading || diff.loading ? (
                <div style={{ padding: 32, color: B.whiteGray }}>Building prompt snapshot…</div>
              ) : diff.error ? (
                <div style={{ padding: 22, color: B.red }}>{diff.error}</div>
              ) : (
                <pre data-testid="prompt-diff-content" style={{
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                  background: B.card, border: `1px solid ${B.border}`, borderRadius: 8,
                  padding: 16, color: B.white, fontSize: 12, lineHeight: 1.55,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  margin: "16px 0 0",
                }}>{diff.system_prompt}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
